import { useCallback, useEffect, useState } from 'react';

import {
  error as commonError,
  utils as commonUtils,
} from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { useUploadContextArtifactInstance } from 'packages/dataPipeline/hooks/UploadContext';

export type ServerState = DataPipeline.Instance['status'];

export const clientUploadStateIterable = [
  'client_initial',
  'client_started',
  'client_uploaded',
  'client_error',
] as const;

export const serverUploadStateIterable: readonly ServerState[] = [
  'initializing',
  'inProgress',
  'success',
  'error',
] as const;

export const UploadStateIterable = [
  ...clientUploadStateIterable,
  ...serverUploadStateIterable,
] as const;

// uploads go thru various states. Following is list (in order) of states
export type UploadState = typeof UploadStateIterable[number];

// a failed upload can be sent again only if it failed before the server
// claimed the input; a claimed input takes no more uploads for this version.
const canRetryUpload = (
  instanceState: UploadState,
  instance: DataPipeline.Instance | null,
): boolean => instanceState === 'client_error' && !instance;

export const useInputNode = ({ artifactId }: { artifactId: string }) => {
  const [instanceState, setInstanceState] = useState<UploadState>(
    'client_initial',
  );

  const [progress, setProgress] = useState<number>(0);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // once we have a server state, we should update the instance state.
  const {
    instance,
    sanitized,
    directUpload,
    directKeep,
    onRunStarting,
    runStarting,
    onUploadAttempted,
  } = useUploadContextArtifactInstance(artifactId);
  const instanceStateFromServer = instance?.status ?? null;
  useEffect(() => {
    if (instanceStateFromServer) {
      // a failed upload leaves the input claimed (`initializing`) on the
      // server; keep showing the failure rather than a spinner that never ends.
      setInstanceState((current) =>
        current === 'client_error' && instanceStateFromServer === 'initializing'
          ? current
          : instanceStateFromServer,
      );
    }
  }, [instanceStateFromServer]);

  // runs one attempt to send the input through the upload states
  const attempt = useCallback(
    async (send: () => Promise<void>): Promise<void> => {
      setErrorMessage(null);
      setInstanceState('client_started');
      try {
        await send();
        setInstanceState('client_uploaded');
        setProgress(0);
        onUploadAttempted();
      } catch (e) {
        setErrorMessage(`upload failed:${commonError.getErrorMessage(e)}`);
        setInstanceState('client_error');
        setProgress(0);
        // learn whether the server claimed the input before the failure.
        onUploadAttempted();
      }
    },
    [onUploadAttempted],
  );

  const upload = useCallback(
    (file: File): Promise<void> =>
      attempt(() =>
        directUpload({
          file,
          onEventCallback: ({ loaded, total }) =>
            setProgress(commonUtils.divideByOrZero(loaded, total) * 100),
        }),
      ),
    [attempt, directUpload],
  );

  // fills the input with the file an earlier version received
  const keep = useCallback(
    (fromFileVersion: number): Promise<void> =>
      attempt(() => directKeep({ fromFileVersion })),
    [attempt, directKeep],
  );

  const cashUploadStarted = () => {
    setInstanceState('client_started');
  };
  const cashUploadEnded = () => {
    setInstanceState('client_uploaded');
    // a cash balance is not sent through a submission, so it starts a new
    // version's run without telling the drawer.
    onRunStarting?.();
    onUploadAttempted();
  };
  const cashUploadFailed = (message: string) => {
    setErrorMessage(`upload failed:${message}`);
    setInstanceState('client_error');
  };

  // the file the input received, once stored; a synthetic instance has none.
  const downloadableInstanceId =
    instance && !instance.synthetic && instance.status === 'success'
      ? instance.id
      : null;

  // upload is enabled until the server has an instance for this input, and held
  // back while another input is starting the run.
  const uploadNeeded =
    !runStarting &&
    (instanceState === 'client_initial' ||
      canRetryUpload(instanceState, instance));

  return {
    upload,
    keep,
    instanceState,
    sanitized,
    errorMessage,
    progress,
    cashUploadStarted,
    cashUploadEnded,
    cashUploadFailed,
    uploadNeeded,
    canRetry: canRetryUpload(instanceState, instance),
    downloadableInstanceId,
  };
};
